import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types/content";

const slides = [
  {
    id: "w14",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20wide%20film%20still%20of%20a%20snowy%20mountain%20village%20at%20dawn%20with%20warm%20orange%20sunrise%20breaking%20through%20deep%20blue%20gray%20mist%2C%20dramatic%20atmospheric%20lighting%2C%20minimalist%20artistic%20composition%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=okuda-hero-01&orientation=landscape",
    tag: "最新上映",
    title: "融雪的清晨",
    meta: "2024 · 剧情电影 · 饰 远野千鹤",
    desc: "雪化了，村子的路终于通了。一次迟到多年的告别，在晨雾散尽时缓缓展开。",
  },
  {
    id: "w09",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20wide%20film%20still%20of%20an%20old%20wooden%20train%20platform%20at%20dusk%20with%20warm%20orange%20sunlight%20and%20deep%20blue%20gray%20shadows%2C%20soft%20film%20grain%2C%20nostalgic%20melancholic%20mood%2C%20artistic%20minimalist%20composition%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=okuda-hero-02&orientation=landscape",
    tag: "代表作",
    title: "追忆叙事曲",
    meta: "2023 · 剧情电影 · 饰 水野沙罗",
    desc: "一位钢琴调音师循着一首旧曲，串联起三代人关于回忆与遗憾的故事。",
  },
  {
    id: "w13",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20wide%20film%20still%20of%20a%20dim%20room%20divided%20by%20harsh%20light%20and%20deep%20shadow%2C%20high%20contrast%20blue%20gray%20and%20warm%20orange%20tones%2C%20chiaroscuro%20lighting%2C%20tense%20dramatic%20atmosphere%2C%20artistic%20composition%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=okuda-hero-03&orientation=landscape",
    tag: "口碑剧集",
    title: "光与影之间",
    meta: "2024 · 悬疑剧集 · 饰 桐生明里",
    desc: "当看见他人选择的能力开始反噬，她必须决定，要不要继续当那个旁观者。",
  },
];

export default function HeroCarousel({ works }: { works: Work[] }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const go = (idx: number) => setCurrent((idx + slides.length) % slides.length);

  return (
    <section className="relative w-full h-[92vh] min-h-[560px] max-h-[860px] overflow-hidden bg-primary-950">
      {slides.map((slide, idx) => {
        const exists = works.some((w) => w.id === slide.id);
        const target = exists ? `/works/${slide.id}` : "/works";
        return (
          <div
            key={slide.id}
            className={[
              "absolute inset-0 transition-opacity duration-1000",
              idx === current ? "opacity-100" : "opacity-0 pointer-events-none",
            ].join(" ")}
          >
            <img
              src={slide.image}
              alt={`${slide.title} - 奥田咲影视作品焦点图`}
              title={`${slide.title} 奥田咲影视作品`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/25"></div>
          </div>
        );
      })}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-7xl mx-auto">
          <div key={current} className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-500 text-background-50 text-xs font-semibold tracking-wide">
              <i className="ri-fire-line"></i>
              {slides[current].tag}
            </span>
            <h2 className="mt-5 font-heading text-4xl md:text-6xl font-bold text-white leading-tight">
              {slides[current].title}
            </h2>
            <p className="mt-4 text-sm md:text-base text-white/75">{slides[current].meta}</p>
            <p className="mt-5 text-base md:text-lg text-white/85 leading-8 max-w-xl hidden sm:block">
              {slides[current].desc}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={works.some((w) => w.id === slides[current].id) ? `/works/${slides[current].id}` : "/works"}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 text-primary-800 text-sm font-semibold hover:bg-accent-500 hover:text-background-50 transition-colors whitespace-nowrap"
              >
                查看作品详情
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/works"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/40 text-white text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                <i className="ri-film-line"></i>
                全部影视作品
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 z-10 px-4 md:px-8">
        <div className="w-full max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {slides.map((slide, idx) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`切换到 ${slide.title}`}
                onClick={() => go(idx)}
                className={[
                  "h-1.5 rounded-full transition-all cursor-pointer",
                  idx === current ? "w-8 bg-accent-500" : "w-4 bg-white/45 hover:bg-white/70",
                ].join(" ")}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => go(current - 1)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/35 text-white hover:bg-white/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => go(current + 1)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/35 text-white hover:bg-white/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent"></div>
    </section>
  );
}